import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useUserSettings } from "@/src/hooks/useUserSettings.ts";
import CustomSelect from "@/src/components/CustomSelect.tsx";
import FilenameTemplateBuilder from "@/src/components/FilenameTemplateBuilder.tsx";
import { SavingIndicator } from "@/src/components/SavingIndicator.tsx";

const GeneralSettings: React.FC = () => {
  const { t } = useTranslation();
  const {
    isSaving,
    softLighting,
    setSoftLighting,
    neverHideParts,
    setNeverHideParts,
    alwaysCenter,
    setAlwaysCenter,
    desiredRoomStyle,
    setDesiredRoomStyle,
    namingTemplate,
    setNamingTemplate,
  } = useUserSettings();

  const roomStyleOptions = [
    { value: "european", label: t("settings.roomEuropean") },
    { value: "american", label: t("settings.roomAmerican") },
    { value: "asian", label: t("settings.roomAsian") },
  ];

  return (
    <div className="space-y-8">
      <SavingIndicator isSaving={isSaving} />
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {t("settings.style")}
        </h2>
        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-6">
          <div className="space-y-4">
            {(
              [
                {
                  value: softLighting,
                  setValue: setSoftLighting,
                  label: t("settings.softLightingLabel"),
                },
                {
                  value: neverHideParts,
                  setValue: setNeverHideParts,
                  label: t("settings.neverHidePartsLabel"),
                },
                {
                  value: alwaysCenter,
                  setValue: setAlwaysCenter,
                  label: t("settings.alwaysCenterLabel"),
                },
              ] as const
            ).map((toggle) => (
              <button
                key={toggle.label}
                type="button"
                role="switch"
                aria-checked={toggle.value}
                onClick={() => toggle.setValue(!toggle.value)}
                className="w-full flex items-center justify-between gap-3 cursor-pointer group text-left"
              >
                <span className="text-sm font-medium text-gray-700 group-hover:text-black transition-colors">
                  {toggle.label}
                </span>
                <div
                  className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors ${toggle.value ? "bg-black" : "bg-gray-200"}`}
                >
                  <span
                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-0 transition-transform duration-200 ${toggle.value ? "translate-x-4" : "translate-x-0"}`}
                  />
                </div>
              </button>
            ))}
          </div>

          <div className="pt-4 border-t border-gray-100">
            <label className="text-sm font-semibold text-gray-700 block mb-2">
              {t("settings.desiredRoomStyle")}
            </label>
            <CustomSelect
              options={roomStyleOptions}
              value={desiredRoomStyle}
              onChange={setDesiredRoomStyle}
              buttonClassName="w-64 text-left p-2.5 bg-white border border-gray-300 rounded-lg text-sm flex items-center justify-between focus:ring-1 focus:ring-black focus:border-black"
            />
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {t("settings.fileNaming")}
        </h2>
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <FilenameTemplateBuilder
            value={namingTemplate}
            onChange={setNamingTemplate}
          />
        </div>
      </section>
    </div>
  );
};

export const Route = createFileRoute("/settings/general")({
  component: GeneralSettings,
});
